import React, { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CompetitorComparisonSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const [selectedCompetitor, setSelectedCompetitor] = useState('competitor1');
  const [months, setMonths] = useState(12);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const neuroAgentsPrice = 197;

  const competitorPrices: Record<
    string,
    { name: string; price: number; setup: number }
  > = {
    competitor1: { name: 'Concorrente A', price: 497, setup: 997 },
    competitor2: { name: 'Concorrente B', price: 397, setup: 0 },
    competitor3: { name: 'Concorrente C', price: 597, setup: 1497 },
    competitor4: { name: 'Concorrente D', price: 347, setup: 497 },
    custom: { name: 'Outro', price: 400, setup: 500 },
  };

  const [customPrice, setCustomPrice] = useState(400);
  const [customSetup, setCustomSetup] = useState(500);

  const currentCompetitor = competitorPrices[selectedCompetitor];
  const competitorMonthlyPrice =
    selectedCompetitor === 'custom' ? customPrice : currentCompetitor.price;
  const competitorSetupFee =
    selectedCompetitor === 'custom' ? customSetup : currentCompetitor.setup;

  const monthlySavings = competitorMonthlyPrice - neuroAgentsPrice;
  const totalSavings = monthlySavings * months + competitorSetupFee;
  const percentageSavings = Math.round(
    (monthlySavings / competitorMonthlyPrice) * 100
  );

  const features = [
    { name: 'Atendimento 24/7 WhatsApp', neuroChat: true, competitor1: true, competitor2: true },
    { name: 'IA Conversacional Avançada', neuroChat: true, competitor1: false, competitor2: true },
    { name: 'Follow-up Automático', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'CRM Integrado', neuroChat: true, competitor1: true, competitor2: false },
    { name: 'Disparos em Massa', neuroChat: true, competitor1: true, competitor2: true },
    { name: 'Instagram + Facebook', neuroChat: true, competitor1: false, competitor2: true },
    { name: 'Email Marketing', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'Múltiplos Agentes IA', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'Dashboard Completo', neuroChat: true, competitor1: true, competitor2: true },
    { name: 'API para Integrações', neuroChat: true, competitor1: true, competitor2: false },
    { name: 'Suporte Humanizado', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'Implementação Gratuita', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'Consultoria Estratégica', neuroChat: true, competitor1: false, competitor2: false },
    { name: 'Sem Taxa de Setup', neuroChat: true, competitor1: false, competitor2: true },
  ];

  const competitors = [
    { name: 'NeuroAgents', price: 'R$197', highlight: true },
    { name: 'Concorrente A', price: 'R$497', highlight: false },
    { name: 'Concorrente B', price: 'R$397', highlight: false },
  ];

  const advantages = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: '60% Mais Barato',
      description: 'Melhor custo-benefício do mercado',
    },
    {
      icon: 'ri-cpu-line',
      title: 'IA Mais Avançada',
      description: 'Tecnologia de ponta em conversação',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Suporte Real',
      description: 'Pessoas reais te ajudando',
    },
    {
      icon: 'ri-gift-line',
      title: 'Mais Recursos',
      description: 'Funcionalidades exclusivas incluídas',
    },
  ];

  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      const primaryColor: [number, number, number] = [16, 185, 129];
      const darkColor: [number, number, number] = [17, 24, 39];
      const grayColor: [number, number, number] = [107, 114, 128];
      const greenColor: [number, number, number] = [34, 197, 94];
      const redColor: [number, number, number] = [239, 68, 68];

      // Header
      doc.setFillColor(...darkColor);
      doc.rect(0, 0, 210, 45, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('NeuroAgents', 20, 25);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...primaryColor);
      doc.text('Comparativo de Mercado', 20, 35);

      // Date
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      const today = new Date().toLocaleDateString('pt-BR');
      doc.text(`Gerado em: ${today}`, 150, 35);

      // Savings Summary Box
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(15, 55, 180, 40, 3, 3, 'F');
      doc.setDrawColor(...primaryColor);
      doc.roundedRect(15, 55, 180, 40, 3, 3, 'S');

      doc.setTextColor(...darkColor);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Sua Economia Estimada', 25, 68);

      doc.setFontSize(24);
      doc.setTextColor(...primaryColor);
      doc.text(`R$ ${totalSavings.toLocaleString('pt-BR')}`, 25, 82);

      doc.setFontSize(10);
      doc.setTextColor(...grayColor);
      doc.text(
        `em ${months} ${months === 1 ? 'mês' : 'meses'} comparado com ${
          selectedCompetitor === 'custom'
            ? 'concorrente'
            : currentCompetitor.name
        }`,
        25,
        90
      );

      // Savings Details
      doc.setFontSize(10);
      doc.setTextColor(...darkColor);
      doc.text(`Economia mensal: R$ ${monthlySavings}/mês`, 120, 70);
      doc.text(
        `Taxa de setup evitada: R$ ${competitorSetupFee.toLocaleString('pt-BR')}`,
        120,
        80
      );
      doc.text(`Você paga ${percentageSavings}% menos`, 120, 90);

      // Comparison Table Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('Tabela Comparativa de Recursos', 15, 115);

      // Table Header
      const tableStartY = 125;
      const rowHeight = 10;

      doc.setFillColor(...darkColor);
      doc.rect(15, tableStartY, 180, rowHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Recurso', 20, tableStartY + 7);
      doc.text('NeuroAgents', 100, tableStartY + 7);
      doc.text('Conc. A', 135, tableStartY + 7);
      doc.text('Conc. B', 170, tableStartY + 7);

      // Price row
      doc.setFillColor(245, 245, 245);
      doc.rect(15, tableStartY + rowHeight, 180, rowHeight, 'F');
      doc.setTextColor(...grayColor);
      doc.setFont('helvetica', 'normal');
      doc.text('Preço mensal', 20, tableStartY + rowHeight + 7);
      doc.setTextColor(...primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.text('R$197', 100, tableStartY + rowHeight + 7);
      doc.setTextColor(...grayColor);
      doc.text('R$497', 135, tableStartY + rowHeight + 7);
      doc.text('R$397', 170, tableStartY + rowHeight + 7);

      // Feature rows
      let currentY = tableStartY + rowHeight * 2;

      features.forEach((feature, index) => {
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }

        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(15, currentY, 180, rowHeight, 'F');
        }

        doc.setTextColor(...darkColor);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(feature.name, 20, currentY + 7);

        // NeuroAgents
        doc.setTextColor(...(feature.neuroChat ? greenColor : redColor));
        doc.text(feature.neuroChat ? '✓' : '✗', 107, currentY + 7);

        // Competitor 1
        doc.setTextColor(...(feature.competitor1 ? greenColor : redColor));
        doc.text(feature.competitor1 ? '✓' : '✗', 142, currentY + 7);

        // Competitor 2
        doc.setTextColor(...(feature.competitor2 ? greenColor : redColor));
        doc.text(feature.competitor2 ? '✓' : '✗', 177, currentY + 7);

        currentY += rowHeight;
      });

      // Total row
      doc.setFillColor(...darkColor);
      doc.rect(15, currentY, 180, rowHeight, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Total de Recursos', 20, currentY + 7);
      doc.setTextColor(...primaryColor);
      doc.text('14/14', 102, currentY + 7);
      doc.setTextColor(150, 150, 150);
      doc.text('5/14', 140, currentY + 7);
      doc.text('6/14', 175, currentY + 7);

      // Footer
      const footerY = currentY + 25;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, footerY, 195, footerY);

      doc.setFontSize(10);
      doc.setTextColor(...primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.text(
        'NeuroAgents - Automação Inteligente para seu Negócio',
        15,
        footerY + 10
      );

      doc.setTextColor(...grayColor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(
        'Mais recursos, menor preço. A escolha inteligente para seu negócio.',
        15,
        footerY + 18
      );

      // Save
      doc.save('NeuroAgents-Comparativo.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 bg-black">
      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 bg-primary/20 text-primary text-xs sm:text-sm font-semibold rounded-full mb-4">
            COMPARATIVO
          </span>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Por que escolher o{' '}
            <span className="text-primary">NeuroAgents</span>?
          </h2>
          <p className="text-sm sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
            Veja como nos comparamos com outras soluções do mercado
          </p>
        </div>

        {/* Advantages Cards - Mobile First */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '150ms' }}
        >
          {advantages.map((adv, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-4 sm:p-5 text-center"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-primary/30 rounded-full mx-auto mb-2 sm:mb-3">
                <i className={`${adv.icon} text-xl sm:text-2xl text-primary`}></i>
              </div>
              <h4 className="text-sm sm:text-base font-bold text-white mb-1">
                {adv.title}
              </h4>
              <p className="text-xs text-gray-400 hidden sm:block">
                {adv.description}
              </p>
            </div>
          ))}
        </div>

        {/* Savings Calculator */}
        <div
          className={`bg-gradient-to-br from-dark-secondary to-dark border border-dark-border rounded-xl sm:rounded-2xl p-5 sm:p-8 mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-accent-yellow/20 rounded-full mx-auto mb-3">
              <i className="ri-calculator-line text-2xl sm:text-3xl text-accent-yellow"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
              Calculadora de Economia
            </h3>
            <p className="text-sm text-gray-400">
              Descubra quanto você economiza ao escolher o NeuroAgents
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Left - Inputs */}
            <div className="space-y-5">
              {/* Competitor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Qual ferramenta você usa ou está considerando?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(competitorPrices).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedCompetitor(key)}
                      className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        selectedCompetitor === key
                          ? 'bg-primary text-white'
                          : 'bg-dark-border text-gray-400 hover:bg-dark-border/80'
                      }`}
                    >
                      {value.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Price Input */}
              {selectedCompetitor === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Mensalidade (R$)
                    </label>
                    <input
                      type="number"
                      value={customPrice}
                      onChange={(e) =>
                        setCustomPrice(Number(e.target.value))
                      }
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Taxa de Setup (R$)
                    </label>
                    <input
                      type="number"
                      value={customSetup}
                      onChange={(e) =>
                        setCustomSetup(Number(e.target.value))
                      }
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                      min={0}
                    />
                  </div>
                </div>
              )}

              {/* Months Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-300">
                    Período de uso
                  </label>
                  <span className="text-sm font-bold text-primary">
                    {months} meses
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={24}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full h-2 bg-dark-border rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 mês</span>
                  <span>12 meses</span>
                  <span>24 meses</span>
                </div>
              </div>

              {/* Price Comparison Visual */}
              <div className="bg-dark rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-400">
                    Comparação mensal
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">
                        {selectedCompetitor === 'custom'
                          ? 'Outro'
                          : currentCompetitor.name}
                      </span>
                      <span className="text-red-400 font-semibold">
                        R${competitorMonthlyPrice}/mês
                      </span>
                    </div>
                    <div className="h-3 bg-dark-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500/60 rounded-full"
                        style={{ width: '100%' }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">NeuroAgents</span>
                      <span className="text-primary font-semibold">
                        R${neuroAgentsPrice}/mês
                      </span>
                    </div>
                    <div className="h-3 bg-dark-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{
                          width: `${(neuroAgentsPrice / competitorMonthlyPrice) *
                            100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Results */}
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-5 sm:p-6">
              <div className="text-center mb-5">
                <span className="inline-block px-3 py-1 bg-accent-yellow/20 text-accent-yellow text-xs font-bold rounded-full mb-3">
                  SUA ECONOMIA
                </span>
                <div className="text-4xl sm:text-5xl font-black text-white mb-1">
                  R${totalSavings.toLocaleString('pt-BR')}
                </div>
                <p className="text-sm text-gray-400">
                  em {months}{' '}
                  {months === 1 ? 'mês' : 'meses'}
                </p>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex justify-between items-center py-2 border-b border-primary/20">
                  <span className="text-sm text-gray-400">
                    Economia mensal
                  </span>
                  <span className="text-lg font-bold text-primary">
                    R${monthlySavings}/mês
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-primary/20">
                  <span className="text-sm text-gray-400">
                    Economia em mensalidades
                  </span>
                  <span className="text-lg font-bold text-white">
                    R${(monthlySavings * months).toLocaleString('pt-BR')}
                  </span>
                </div>
                {competitorSetupFee > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-primary/20">
                    <span className="text-sm text-gray-400">
                      Taxa de setup evitada
                    </span>
                    <span className="text-lg font-bold text-accent-yellow">
                      R${competitorSetupFee.toLocaleString('pt-BR')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-400">
                    Você paga menos
                  </span>
                  <span className="text-lg font-bold text-green-400">
                    {percentageSavings}%
                  </span>
                </div>
              </div>

              <div className="bg-dark/50 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500/20 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-green-400"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-300">
                      <strong className="text-white">
                        Com essa economia você pode:
                      </strong>{' '}
                      investir em tráfego pago, contratar mais
                      funcionários ou aumentar seu lucro!
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="#planos"
                className="flex items-center justify-center gap-2 w-full py-3 sm:py-4 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg transition-all cursor-pointer text-sm sm:text-base whitespace-nowrap"
              >
                Começar a Economizar
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div
          className={`bg-dark-secondary border border-dark-border rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '300ms' }}
        >
          {/* Table Header */}
          <div className="grid grid-cols-4 bg-dark border-b border-dark-border">
            <div className="p-3 sm:p-5 text-left">
              <span className="text-xs sm:text-sm font-semibold text-gray-400">
                Recursos
              </span>
            </div>
            {competitors.map((comp, index) => (
              <div
                key={index}
                className={`p-3 sm:p-5 text-center ${comp.highlight ? 'bg-primary/10' : ''}`}
              >
                <div
                  className={`text-xs sm:text-base font-bold ${
                    comp.highlight ? 'text-primary' : 'text-gray-300'
                  }`}
                >
                  {comp.name}
                </div>
                <div
                  className={`text-sm sm:text-lg font-black mt-1 ${
                    comp.highlight ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {comp.price}
                  <span className="text-xs font-normal text-gray-500">
                    /mês
                  </span>
                </div>
                {comp.highlight && (
                  <span className="inline-block mt-2 px-2 py-0.5 bg-accent-yellow text-black text-[10px] sm:text-xs font-bold rounded-full">
                    MELHOR ESCOLHA
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Table Body */}
          <div className="divide-y divide-dark-border">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid grid-cols-4 transition-all duration-500 ${
                  isVisible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ transitionDelay: `${400 + index * 50}ms` }}
              >
                <div className="p-3 sm:p-4 flex items-center">
                  <span className="text-[11px] sm:text-sm text-gray-300 leading-tight">
                    {feature.name}
                  </span>
                </div>
                <div
                  className={`p-3 sm:p-4 flex items-center justify-center ${
                    feature.neuroChat ? 'bg-primary/5' : ''
                  }`}
                >
                  {feature.neuroChat ? (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-green-500/20 rounded-full">
                      <i className="ri-check-line text-sm sm:text-lg text-green-400"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-red-500/10 rounded-full">
                      <i className="ri-close-line text-sm sm:text-lg text-red-400/50"></i>
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 flex items-center justify-center">
                  {feature.competitor1 ? (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-green-500/10 rounded-full">
                      <i className="ri-check-line text-sm sm:text-lg text-green-400/60"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-red-500/10 rounded-full">
                      <i className="ri-close-line text-sm sm:text-lg text-red-400/50"></i>
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 flex items-center justify-center">
                  {feature.competitor2 ? (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-green-500/10 rounded-full">
                      <i className="ri-check-line text-sm sm:text-lg text-green-400/60"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-red-500/10 rounded-full">
                      <i className="ri-close-line text-sm sm:text-lg text-red-400/50"></i>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Table Footer - Summary */}
          <div className="grid grid-cols-4 bg-dark border-t border-dark-border">
            <div className="p-3 sm:p-5 flex items-center">
              <span className="text-xs sm:text-sm font-bold text-white">
                Total de Recursos
              </span>
            </div>
            <div className="p-3 sm:p-5 flex items-center justify-center bg-primary/10">
              <span className="text-lg sm:text-2xl font-black text-primary">
                14/14
              </span>
            </div>
            <div className="p-3 sm:p-5 flex items-center justify-center">
              <span className="text-lg sm:text-2xl font-black text-gray-500">
                5/14
              </span>
            </div>
            <div className="p-3 sm:p-5 flex items-center justify-center">
              <span className="text-lg sm:text-2xl font-black text-gray-500">
                6/14
              </span>
            </div>
          </div>
        </div>

        {/* Download PDF Button */}
        <div
          className={`flex justify-center mt-6 sm:mt-8 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '500ms' }}
        >
          <button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            className="flex items-center gap-2 px-5 sm:px-6 py-3 bg-dark-secondary hover:bg-dark-border border border-dark-border text-white font-medium rounded-lg transition-all cursor-pointer text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg"></i>
                <span>Gerando PDF...</span>
              </>
            ) : (
              <>
                <i className="ri-file-pdf-2-line text-lg text-red-400"></i>
                <span>Baixar Comparativo em PDF</span>
              </>
            )}
          </button>
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-8 sm:mt-12 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          <p className="text-gray-400 text-xs sm:text-base mb-4 sm:mb-6">
            <strong className="text-white">
              Mais recursos, menor preço.
            </strong>{' '}
            A escolha é óbvia.
          </p>
          <a
            href="#planos"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
          >
            Ver Planos
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
